import { useEffect, useState } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { Building2, Plus, Pencil, Trash2, MapPin, MoreVertical, LayoutGrid, List, DoorOpen } from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api, type Building, type Room } from '../lib/api'
import { DashboardLayout } from '../components/DashboardLayout'
import { PageTabs } from '../components/PageTabs'

export function BuildingsPage() {
  const navigate = useNavigate()
  const { user, currentOrg, isAuthenticated, isLoading: authLoading, checkAuth } = useAuthStore()
  const [buildings, setBuildings] = useState<Building[]>([])
  const [buildingRooms, setBuildingRooms] = useState<Map<string, number>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    const saved = localStorage.getItem('roomsi-buildings-view')
    return (saved === 'list' || saved === 'cards') ? saved : 'cards'
  })
  
  useEffect(() => {
    checkAuth()
  }, [])
  
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [authLoading, isAuthenticated, navigate])
  
  // Redirect non-managers - only admins and approvers can manage buildings
  useEffect(() => {
    if (
      !authLoading &&
      isAuthenticated &&
      currentOrg &&
      currentOrg.role !== 'admin' &&
      currentOrg.role !== 'approver'
    ) {
      navigate({ to: '/dashboard' })
    }
  }, [authLoading, isAuthenticated, currentOrg, navigate])
  
  useEffect(() => {
    if (isAuthenticated) {
      loadBuildings()
    }
  }, [isAuthenticated])

  const loadBuildings = async () => {
    try {
      setIsLoading(true)
      const [buildingsData, roomsData] = await Promise.all([
        api.buildings.list(),
        api.rooms.list(),
      ])
      // Sort buildings alphabetically by name
      const sortedBuildings = [...buildingsData.buildings].sort((a, b) => 
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
      )
      setBuildings(sortedBuildings)
      
      // Count rooms per building
      const roomCounts = new Map<string, number>()
      roomsData.rooms.forEach((room: Room) => {
        if (room.buildingId) {
          roomCounts.set(room.buildingId, (roomCounts.get(room.buildingId) || 0) + 1)
        }
      })
      setBuildingRooms(roomCounts)
    } catch (err) {
      console.error('Failed to load buildings:', err)
      setError('Failed to load buildings')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (buildingId: string, buildingName: string) => {
    if (!confirm(`Delete "${buildingName}"? This cannot be undone.`)) return
    
    try {
      await api.buildings.delete(buildingId)
      setBuildings(buildings.filter(b => b.id !== buildingId))
    } catch (err: any) {
      setError(err.message || 'Failed to delete building')
    }
  }
  
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }
  
  if (!isAuthenticated || !user) {
    return null
  }

  const isAdmin = currentOrg?.role === 'admin'

  const headerActions = (
    <>
      {/* View toggle */}
      <div className="flex items-center bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => { setViewMode('cards'); localStorage.setItem('roomsi-buildings-view', 'cards'); }}
          className={`p-1.5 rounded ${viewMode === 'cards' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
          title="Card view"
        >
          <LayoutGrid className="w-4 h-4" />
        </button>
        <button
          onClick={() => { setViewMode('list'); localStorage.setItem('roomsi-buildings-view', 'list'); }}
          className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
          title="List view"
        >
          <List className="w-4 h-4" />
        </button>
      </div>
      {isAdmin && (
        <Link
          to="/dashboard/buildings/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Building
        </Link>
      )}
    </>
  )

  return (
    <DashboardLayout title="Rooms" actions={headerActions}>
      <PageTabs
        tabs={[
          { label: 'Rooms', to: '/dashboard/rooms' },
          { label: 'Buildings', to: '/dashboard/buildings' },
        ]}
        active="/dashboard/buildings"
      />
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}
      
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : buildings.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No buildings yet</h3>
          <p className="text-gray-500 mb-6">Get started by adding your first building</p>
          {isAdmin && (
            <Link
              to="/dashboard/buildings/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Building
            </Link>
          )}
        </div>
      ) : viewMode === 'cards' ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {buildings.map(building => (
            <BuildingCard
              key={building.id}
              building={building}
              roomCount={buildingRooms.get(building.id) || 0}
              isAdmin={isAdmin}
              onDelete={() => handleDelete(building.id, building.name)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 sm:px-4 py-2 sm:py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Building</th>
                <th className="hidden sm:table-cell text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Location</th>
                <th className="text-center sm:text-left px-2 sm:px-4 py-2 sm:py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Rooms</th>
                <th className="text-right px-2 sm:px-4 py-2 sm:py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {buildings.map(building => (
                <BuildingRow
                  key={building.id}
                  building={building}
                  roomCount={buildingRooms.get(building.id) || 0}
                  isAdmin={isAdmin}
                  onDelete={() => handleDelete(building.id, building.name)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  )
}

function BuildingCard({
  building,
  roomCount,
  isAdmin,
  onDelete,
}: {
  building: Building
  roomCount: number
  isAdmin: boolean
  onDelete: () => void
}) {
  const [showMenu, setShowMenu] = useState(false)
  const navigate = useNavigate()
  
  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-menu]')) return
    navigate({ to: `/dashboard/buildings/${building.id}/edit` })
  }
  
  const displayLocation = [building.city, building.postcode].filter(Boolean).join(', ') || building.address
  
  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Header with icon */}
      <div className="h-32 bg-gray-100 flex items-center justify-center">
        <Building2 className="w-16 h-16 text-primary-300" />
      </div>
      
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-gray-900">{building.name}</h3>
          {isAdmin && (
            <div className="relative" data-menu>
              <button 
                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-[120px]">
                    <Link
                      to={`/dashboard/buildings/${building.id}/edit`}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                    </Link>
                    <button
                      onClick={() => { setShowMenu(false); onDelete(); }}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        
        {displayLocation && (
          <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
            <MapPin className="w-4 h-4" />
            <span className="truncate">{displayLocation}</span>
          </div>
        )}
        
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <DoorOpen className="w-4 h-4" />
          <span>{roomCount} {roomCount === 1 ? 'room' : 'rooms'}</span>
        </div>
      </div>
    </div>
  )
}

function BuildingRow({
  building,
  roomCount,
  isAdmin,
  onDelete,
}: {
  building: Building
  roomCount: number
  isAdmin: boolean
  onDelete: () => void
}) {
  const [showMenu, setShowMenu] = useState(false)
  const navigate = useNavigate()
  
  const handleRowClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-menu]')) return
    navigate({ to: `/dashboard/buildings/${building.id}/edit` })
  }
  
  const displayLocation = [building.city, building.postcode].filter(Boolean).join(', ') || building.address || '-'
  
  return (
    <tr 
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={handleRowClick}
    >
      <td className="px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-primary-600" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-gray-900 text-sm sm:text-base truncate">{building.name}</div>
            <div className="text-xs text-gray-500 sm:hidden truncate">{displayLocation !== '-' && displayLocation}</div>
          </div>
        </div>
      </td>
      <td className="hidden sm:table-cell px-4 py-3 text-sm text-gray-600">
        {displayLocation}
      </td>
      <td className="px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-600 text-center sm:text-left">
        {roomCount}
      </td>
      <td className="px-2 sm:px-4 py-2 sm:py-3 text-right">
        {isAdmin && (
          <div className="relative inline-block" data-menu onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-[120px]">
                  <Link
                    to={`/dashboard/buildings/${building.id}/edit`}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </Link>
                  <button
                    onClick={() => { setShowMenu(false); onDelete(); }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}
